// Copyright (c) 2021-2025 Broadcom. All Rights Reserved.
// Broadcom Confidential. The term "Broadcom" refers to Broadcom Inc.
// and/or its subsidiaries.
import { AfterViewInit, Component, OnDestroy, OnInit, Renderer2, ViewChild } from "@angular/core";
import { MaintenanceScheduleStateEnum, SeverityEnum, UINotificationMessage, UINotificationMessageParameter, UINotificationMessageParameterDataTypeEnum } from "@api/generated/vcdr";
import { ReplaySubject, Subject, Subscription } from "rxjs";
import { Router } from "@angular/router";
import { Title } from "@angular/platform-browser";
import { distinctUntilChanged, map, takeUntil } from "rxjs/operators";
import { CspHeaderComponent, CspHeaderMode, CspHeaderOptions } from "@vmw/csp-ngx-components/header";
import { I18nService, L10nService } from "@vmw/ngx-vip";
import { CspApiService, CspTokenService, Org } from "@vmw/csp-ngx-components";
import { OAuth2Client, OAuth2TokenResponse, StateClaims } from "@vmw/csp-oauth2";
import { AccessTokenSubject, LAST_VISITED_URL_KEY, OAuth2TokenSubject } from "@vmw/ngx-csp-oauth2";
import { environment } from "../environments/environment";
import { DialogAnchorDirective } from "@shared/components/dialog/dialog-anchor/dialog-anchor.directive";
import { ConsoleStrings } from "@shared/models/strings.model";
import { AppConfigService, AppStateService, AUTH_KEYS, Dispatcher, getUser, NotificationService, ResponsiveUiService, ThemeService, User, WindowService } from "@app/services";
import { MaintenanceModeEvent, TitleUpdateEvent } from "@app/services/dispatcher/events";
import { IconService } from "@shared/services";
import { DialogService } from "@shared/components/dialog/dialog.service";
import { OverageEvent } from "@app/services/dispatcher/events/overage-event";
import { BannerInfo, ToastInfo } from "@shared/models/notification.model";

export enum APP_KEYS {
    CSP_AUTH_TOKEN = "csp-auth-token",
    CSP_INSTANCE = "csp-instance",
    ORG_DISPLAY_NAME = "orgDisplayName",
    ORG_ERP_ID = "orgErpId",
    ERROR_ROUTE = "errorRoute",
    AUTHORIZATION = "Authorization"
}

// @formatter:off
@Component({
    standalone: false,
    selector: 'app',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss']
})
// @formatter:on
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {

    @ViewChild(DialogAnchorDirective, {static: false}) dialogAnchor: DialogAnchorDirective;
    @ViewChild(CspHeaderComponent) cspHeader: CspHeaderComponent;

    public title: string;
    public showHeader: boolean = true;
    public drawerShowing: boolean = false;
    public appLevelBanners: BannerInfo[] = [];
    public appLevelToasts: ToastInfo[] = [];
    public headerOptions: CspHeaderOptions;

    // The header needs this in order to check the service roles in the new Org.
    // If the user does not have any service roles, the header will redirect him to
    // the CSP portal, and will show error. SwitchOrg() will not be called.
    //public serviceLink: string;

    public CspHeaderMode = CspHeaderMode;
    public cspEnvironment = environment.cspEnvironment;

    public accessToken: string;
    public readonly oAuth2TokenSubscription: Subscription;

    private _unsubscribeSubject: Subject<void> = new ReplaySubject<void>(1);

    public headerTitleKey: string;
    public htmlTitleKey: string;

    private IN_TEN_MINUTES_MS: number = 10 * 60 * 1000;
    private refreshTimeout: any;

    constructor(private renderer: Renderer2,
                private themeService: ThemeService,
                private windowService: WindowService,
                private responsiveService: ResponsiveUiService,
                private router: Router,
                private appConfig: AppConfigService,
                private l10nService: L10nService,
                private i18nService: I18nService,
                private titleService: Title,
                private iconService: IconService,
                private dialogService: DialogService,
                private dispatcher: Dispatcher,
                public notificationService: NotificationService,
                public oAuth2Client: OAuth2Client,
                public cspApiService: CspApiService,
                public tokenService: CspTokenService,
                public appStateService: AppStateService,
                accessTokenSubject: AccessTokenSubject,
                oauth2TokenSubject: OAuth2TokenSubject) {
        // Clear local storage.
        this.clearLocalStorage();

        this.headerTitleKey = this.getHeaderTitleKey();
        this.htmlTitleKey = this.getHtmlTitleKey();

        this.headerOptions = this.buildCspHeaderOptions();

        this.cspApiService.fetchCspPublicConfiguration().subscribe(cspPublicConfiguration => {
            if (cspPublicConfiguration) {
                cspPublicConfiguration.customerSupportDisabled = true;
                cspPublicConfiguration.billingDisabled = true;
            }
        });
        // Restart the authorization code flow if needed.
        if (this.handleReauthorization()) {
            return;
        }

        this.iconService.load();

        accessTokenSubject
            .pipe(takeUntil(this._unsubscribeSubject))
            .subscribe(token => {
                this.accessToken = token.raw;
            });

        this.oAuth2TokenSubscription = oauth2TokenSubject.pipe(
            map((token) => token && token.access_token || null),
            distinctUntilChanged()
        ).subscribe((token) => {
            tokenService.authToken = token;
            if (token) {
                // Start refreshing token at a set interval.
                this.refreshTokenAtNextInterval();

                // Set app title based on client type.
                this.setAppTitle();

                this.dispatcher.onTitleUpdate.subscribe(
                    (_event: TitleUpdateEvent) => {
                        setTimeout(() => this.setAppTitle());
                    });

                this.dispatcher.onMaintenanceMode.subscribe(
                    (event: MaintenanceModeEvent) => {
                        this.handleMaintenanceModeEvent(event);
                    }
                );

                this.dispatcher.onOverageStatus.subscribe(
                    (event: OverageEvent) => {
                        this.handleOverageEvent(event);
                    }
                );
            }
            // TODO: do we want to use window.localStorage?
            // TODO: refactor!!!
            window.localStorage.setItem(APP_KEYS.CSP_INSTANCE, "stg");
        });
    }

    ngAfterViewInit(): void {
        // DialogAnchor is the place in the DOM where all dialogs are attached when created.
        this.dialogService.setDialogAnchor(this.dialogAnchor);
        this.windowService.initialize(this.renderer);
        this.responsiveService.init();
        this.themeService.initialize();

        this.cspApiService.fetchUserData().subscribe(val => {
            if (val.preferredTheme) {
                this.themeService.loadTheme(val.preferredTheme.toLowerCase());
                if (this.cspHeader?.themeService) {
                    this.cspHeader.themeService.theme = val.preferredTheme as any;
                }
            }
        })
    }

    /**
     * Clear local storage.
     */
    clearLocalStorage(): void {
        window.localStorage.removeItem(APP_KEYS.CSP_INSTANCE);
        window.sessionStorage.removeItem(APP_KEYS.ORG_DISPLAY_NAME);
        window.sessionStorage.removeItem(APP_KEYS.ORG_ERP_ID);
    }

    /**
     * Restart the auth code flow if the OAuth client org does not match the selected CSP header org.
     */
    handleReauthorization(): boolean {
        const currentOrgId: string = window.sessionStorage.getItem(AUTH_KEYS.ORG_ID);
        const user: User = getUser(this.oAuth2Client);
        const userOrgId: string = user.orgId;

        if (currentOrgId && userOrgId && currentOrgId !== userOrgId) {
            this.oAuth2Client.login({
                applicationState: {},
                orgId: currentOrgId
            });
            return true;
        }

        return false;
    }

    public async orgChange(newOrg: Org): Promise<void> {
        console.log("Org changed to " + newOrg.displayName);

        // Clear local storage.
        this.clearLocalStorage();

        // Clear the CLIENT_TYPE_FROM_PATH key so that the clientType value is not determined from the previous org's
        // client type. Specifically, if changing from the operator org to a non-operator org, do not assume the new
        // org should be loaded with an operator client type.
        window.sessionStorage.removeItem(AUTH_KEYS.CLIENT_TYPE_FROM_PATH);

        // Don't remember the previous pathname value when changing orgs.
        await this.oAuth2Client.login({
            applicationState: {},
            orgId: newOrg.id
        });
    }

    /**
     * Detect CSP org change.
     * Store values in local storage so they persist after the CSP redirect.
     * @param selectedOrg
     */
    public selectedOrgChange(selectedOrg: Org): void {
        window.sessionStorage.setItem(APP_KEYS.ORG_DISPLAY_NAME, selectedOrg.displayName);
        window.sessionStorage.setItem(AUTH_KEYS.ORG_ID, selectedOrg.id);
        if (selectedOrg.customerId) {
            window.sessionStorage.setItem(APP_KEYS.ORG_ERP_ID, selectedOrg.customerId);
        }
    }

    async ngOnInit(): Promise<any> {
        this.cspApiService.setCspEnvironment(this.cspEnvironment);
        this.cspApiService.setCspHost(this.appConfig.cspOAuthConfig.cspHostname);
        if (this.appConfig.redirectPaths) {
            this.cspApiService.setRedirects(this.appConfig.redirectPaths);
        }

        // 1- Authorization response.
        if (this.oAuth2Client.hasAuthorizationResponse()) {
            try {
                return await this.handleAuthorization();
            } catch (queryParams) {
                return await this.handleOauthError(queryParams);
            }
        }

        // 2- Error response.
        if (this.oAuth2Client.hasErrorResponse()) {
            const queryParams = this.oAuth2Client.lastError;
            return await this.handleOauthError(queryParams);
        }

        // No error. Navigate to original path and let the RoleGuard handle routing.
        return await this.router.navigate([window.location.pathname]);
    }

    protected buildCspHeaderOptions(): CspHeaderOptions {
        const headerOptions = new CspHeaderOptions();
        // Most of the values are default
        headerOptions.baseRoute = "/";
        headerOptions.showBackdrop = true;
        headerOptions.showOrgSwitcher = true;
        headerOptions.enableSignout = true;
        headerOptions.showNotificationsMenu = true;
        headerOptions.showLanguageSelector = true;
        headerOptions.enableChangeDefaultOrg = true;
        headerOptions.userMenuIconified = false;
        headerOptions.showHeaderBeforeFullyInitialized = true;
        headerOptions.showHelpMenu = false;
        headerOptions.showSupportTab = false;
        headerOptions.disableDocsSearch = true;
        headerOptions.disableSupportFeedback = true;
        headerOptions.disableOrgBranding = true;
        return headerOptions;
    }

    private async handleAuthorization(): Promise<void> {
        const state: StateClaims = await this.oAuth2Client.validateAuthorizeResponse();

        // If we have a state that came back from the authorization response...
        // eslint-disable-next-line no-prototype-builtins
        if (state.hasOwnProperty(LAST_VISITED_URL_KEY)) {
            const url = state[LAST_VISITED_URL_KEY];
            // Remove OAuth URL with format /oauth2?code=foo&state=bar. Replace with last visited path.
            this.resetUrl(url?.slice(1));

            // Try to navigate there!
            await this.router.navigateByUrl(url);
        } else {
            // Remove OAuth URL with format /oauth2?code=foo&state=bar. Replace with root path.
            this.resetUrl();

            // Otherwise, just use the default route.
            await this.router.navigate([""]);
        }
    }

    /**
     * Refresh the user tokens
     */
    private refreshToken(): void {
        this.oAuth2Client.refresh().then((_tokenResponse: OAuth2TokenResponse) => {
            this.refreshTokenAtNextInterval();
        }).catch((e: Error) => {
            console.error("Auth: Failed to refresh token", e);

            // Get current path.
            const applicationState: { [key: string]: any; } = {};
            applicationState[LAST_VISITED_URL_KEY] = window.location.pathname;

            // Get current org.
            const currentOrgId: string = window.sessionStorage.getItem(AUTH_KEYS.ORG_ID);

            // Initialize the OAuth2Client authorization code flow.
            this.oAuth2Client.login({
                applicationState: applicationState,
                orgId: currentOrgId
            });
        });
    }

    /**
     * Refresh user token after predetermined interval
     */
    private refreshTokenAtNextInterval() {
        clearTimeout(this.refreshTimeout);
        this.refreshTimeout = setTimeout(
            () => {
                this.refreshToken.apply(this);
            },
            this.IN_TEN_MINUTES_MS);
    }

    /**
     * Determine browser tab and application header title values
     */
    private setAppTitle() {
        const htmlTitleLocaleKey: string = this.getHtmlTitleKey();

        // The locale is taken on startup - it is not live
        this.headerOptions.title = this.l10nService.getMessage(htmlTitleLocaleKey);
    }

    private getHeaderTitleKey(): string {
        const headerTitleLocaleKey: string = this.appConfig.getServiceName();
        return headerTitleLocaleKey;
    }

    private getHtmlTitleKey(): string {
        const consoleStrings: ConsoleStrings = this.appConfig.getConsoleStrings();
        return consoleStrings.htmlTitleDefault;
    }

    /**
     * Navigate to error view, then reset URL to prevent OAuth URLs from being saved in history.
     * @param queryParams
     * @return boolean
     */
    private async handleOauthError(queryParams: any): Promise<boolean> {
        await this.router.navigate(["oauth2", "error"], {queryParams});
        return new Promise<boolean>(resolve => {
            // Remove OAuth URL with format /oauth2/error?error=foo&error_description=bar. Replace with root path.
            this.resetUrl();
            // Login again if current token is invalid.
            const currentOrgId: string = window.sessionStorage.getItem(AUTH_KEYS.ORG_ID);
            this.oAuth2Client.login({
                                        applicationState: {},
                                        orgId: currentOrgId
                                    });
            resolve(true);
        });
    }

    private handleMaintenanceModeEvent(event: MaintenanceModeEvent) {
        let appLevelAlert = null;
        const schedule = event.schedule;
        const title = this.l10nService.getMessage(this.getHeaderTitleKey());
        switch (schedule.state) {
            case MaintenanceScheduleStateEnum.CONFIRMED:
                appLevelAlert = this.l10nService.getMessage("console.maintenance.scheduled",
                    [title,
                        this.i18nService.formatDate(schedule.start_datetime,
                            "medium"),
                        this.i18nService.formatDate(schedule.end_datetime,
                            "medium")]);
                break;
            case MaintenanceScheduleStateEnum.STARTED:
                appLevelAlert = this.l10nService.getMessage("console.maintenance.started",
                    [title,
                        this.i18nService.formatDate(schedule.end_datetime,
                            "medium")]);
                break;
        }
        if (appLevelAlert) {
            const existingBanner = this.appLevelBanners.find(bannerInfo => bannerInfo.id === event.id);
            if (existingBanner) {
                existingBanner.message = appLevelAlert;
            } else {
                this.appLevelBanners.push(new BannerInfo(appLevelAlert, SeverityEnum.WARN, event.id));
            }
        }
    }

    private handleOverageEvent(event: OverageEvent): void {
        if (event.overageStatus?.toast_notification) {
            event.overageStatus?.toast_notification.forEach((uiNotification: UINotificationMessage) => {
                const title = this.l10nService.getMessage("console.banner.title.overage");
                const formattedMessage = this.formatMessage(uiNotification);
                const severity = uiNotification.severity === "Warning" ? SeverityEnum.WARN : SeverityEnum.CRITICAL;
                this.appLevelToasts.push(new ToastInfo(title, formattedMessage, severity));
            });
        }
        if (event.overageStatus?.banner_notification) {
            event.overageStatus?.banner_notification.forEach((uiNotification: UINotificationMessage) => {
                const formattedMessage = this.formatMessage(uiNotification);
                const severity = uiNotification.severity === "Warning" ? SeverityEnum.WARN : SeverityEnum.CRITICAL;
                this.appLevelBanners.push(new BannerInfo(formattedMessage, severity));
            });
        }
    }

    /**
     * Modify the URL in the current history entry.
     * @param url
     * @private
     */
    private resetUrl(url: string = ""): void {
        window.history.replaceState({}, window.document.title, url);
    }

    public ngOnDestroy(): void {
        this._unsubscribeSubject.next();
        this._unsubscribeSubject.complete();
    }

    public goToContent(unused: Event): void {
        const mainElements: HTMLCollectionOf<HTMLElement> = document.getElementsByTagName("main");
        if (mainElements && mainElements.length > 0) {
            const main: HTMLElement = mainElements[0];
            if (main) {
                main.setAttribute("tabindex", "-1");
                main.focus();
            }
        }
    }

    private formatParam(param: UINotificationMessageParameter) {
        let formattedParam = param.data_value;
        if (param.data_type === UINotificationMessageParameterDataTypeEnum.DATE) {
            formattedParam = this.i18nService.formatDate(new Date(param.data_value), "mediumDate");
        } else if (param.data_type === UINotificationMessageParameterDataTypeEnum.DATE_TIME) {
            formattedParam = this.i18nService.formatDate(new Date(param.data_value), "medium");
        }
        return formattedParam;
    }

    private formatMessage(uiNotification: UINotificationMessage) {
        let formattedMessage = uiNotification.template;
        uiNotification.params.forEach((param: UINotificationMessageParameter, index) => {
            formattedMessage = formattedMessage?.replace("{" + index + "}", this.formatParam(param));
        });
        return formattedMessage;
    }
}
